/* Split and Rotate tools — added after the original app.js so the
   existing, already-tested merge/compress/Word/PowerPoint code never has
   to be touched. Loaded as a second <script> tag right after app.js, on
   pages that include the Split/Rotate tabs. Reuses the shared helpers
   already defined at the top of app.js (setupDropzone, escapeHtml,
   fmtSize, baseFileName, offerResults,
   updateLargeFileWarning, isProcessing) — those are plain top-level
   function/let declarations in a classic (non-module) script, so they're
   visible here too since both scripts share the page's script scope. */

/* v47: every tool here opens files with openPdfForEdit() (app.part5.js):
   files with only an owner password are decrypted first (they used to come
   out blank or unreadable), and an unreadable file gives the site's normal
   message instead of pdf-lib's raw parser error. */
/* hasCertificateSignature(): in pc-pdfcore.js since v96 (Merge and Compress use it too) */
/* (v90) The notes under a result: files that had an owner password, files
   whose certificate signature is no longer valid, and anything extra. */
function toolNotes(results, extra){
  const names = key => [...new Set(results.filter(r => r[key]).map(r => r.name))];
  const owner = names('decrypted'), cert = names('certSigned'), xfa = names('xfaHybrid');
  return [owner.length ? pcT('ownerNote', owner.join(', ')) : '', cert.length ? pcT('certBroke', cert.join(', ')) : '',
    xfa.length ? pcT('xfaHybridNote', xfa.join(', ')) : '', extra || '']
    .filter(Boolean).join(' ');
}
/* (v90) Progress inside one file: the bar moves, and every so often the page
   gets a moment to draw it and to notice the Cancel button. The output is
   the same as without it. */
const PROGRESS_YIELD_MS = 100;
function toolFileProgress(bar, index, count){
  let last = Date.now();
  return async frac => {
    bar.style.width = Math.round(((index + Math.max(0, Math.min(1, frac))) / count) * 100) + '%';
    if(Date.now() - last < PROGRESS_YIELD_MS) return;
    await new Promise(r => setTimeout(r));
    last = Date.now();
    pcCheckCancel();
  };
}
/* [1,2,4,8,9,10] -> "1-2, 4, 8-10" */
function pageRangesText(nums){
  const parts = [];
  for(let i = 0; i < nums.length; i++){
    let j = i;
    while(j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
    parts.push(i === j ? String(nums[i]) : nums[i] + '-' + nums[j]);
    i = j;
  }
  return parts.join(', ');
}
/* A cancel stops the whole batch instead of being listed as a skipped file. */
function rethrowCancel(err){ if(pcIsCancel(err)) throw err; }
/* (v55) A message meant for the user (pcUserError, app.js) is shown as it
   is. Any other error (a pdf-lib or memory fault) is shown as the site's
   normal, translated message instead of its raw English/technical text. */
function pcReasonOf(err){
  pcLogUnexpected(err);
  return friendlyPdfError(err);
}
// (v56) the page-list parser (pcParsePageSpec, pcResolvePageSpec, pcCheckPageExists)
// and pcFieldError are shared with tools-more.js and live in app.js

/* (v140) The loop of Split, Rotate, Numbers and Delete: `one(file, onProgress)`
   per file (in the worker); a file that fails is skipped with its reason, a
   lone file keeps its own message, none left = the tool's own errSkip.../errNone... */
async function pcEachPdfFile(list, ctx, one, skipKey, noneKey){
  const results = [], skipped = [];
  let fieldHit = false;
  for(let f = 0; f < list.length; f++){
    const file = list[f];
    pcCheckCancel();
    try{
      results.push(...[].concat(await one(file, toolFileProgress(ctx.bar, f, list.length))));
    }catch(err){
      rethrowCancel(err);
      if(err && err.field) fieldHit = true;
      if(list.length === 1) throw err; // one file: its own message, without the "Could not ...: name" prefix
      skipped.push(file.name + ' (' + pcReasonOf(err) + ')');
    }
    ctx.setProgress((f + 1) / list.length);
  }
  if(!results.length) throw Object.assign(pcUserError(skipped.length ? pcT(skipKey, skipped.join(', ')) : pcT(noneKey)), { field: fieldHit });
  return { results, skipped, fieldHit };
}

/* ---------- SPLIT ---------- */
/* (v157) "a new file every N pages", made by script under the split points
   (no page markup changes in 8 languages). */
function splitEveryField(){
  const hint = document.getElementById('split-points-hint');
  if(!hint) return null;
  const lab = document.createElement('label');
  lab.className = 'text-field'; lab.htmlFor = 'split-every'; lab.textContent = pcT('splitEvery');
  const inp = document.createElement('input');
  inp.type = 'text'; inp.id = 'split-every'; inp.className = 'text-input'; inp.autocomplete = 'off';
  inp.inputMode = 'numeric'; inp.placeholder = '2';
  hint.insertAdjacentElement('afterend', inp);
  inp.insertAdjacentElement('beforebegin', lab);
  return inp;
}
const SPLIT_EVERY_RE = /^\s*\d+\s*$/;
function splitEveryValue(inp, points){
  const v = inp ? inp.value.trim() : '';
  if(!v) return 0;
  const fail = key => Object.assign(pcFieldError(pcT(key)), { fieldId: 'split-every' });
  if(!SPLIT_EVERY_RE.test(v) || parseInt(v, 10) < 1) throw fail('splitEveryBad');
  if(points.trim()) throw fail('splitEveryBoth');
  return parseInt(v, 10);
}
pcOnPanel('split', function(){ // (v140) only once its panel is in the page (live, or made from its <template>)
  const pointsInput = document.getElementById('split-points');
  const everyInput = splitEveryField(); // (v157)
  pcSetupTool('split', {
    accept: isPdfFile, working: () => pcT('stSplitting'), zipName: 'split-pdf-files', fieldId: ['split-points', 'split-every'], reason: friendlyPdfError,
    run: async (list, _option, ctx) => {
      const input = pointsInput.value;
      const every = splitEveryValue(everyInput, input);
      const uncovered = [];
      const out = await pcEachPdfFile(list, ctx, async (file, onProgress) => {
        const r = await ctx.worker.run('splitPdf', file, { input, every }, onProgress); // (v113) in the worker when there is one
        if(r.uncovered.length) uncovered.push((list.length > 1 ? file.name + ': ' : '') + pageRangesText(r.uncovered));
        return r.parts;
      }, 'errSkipSplit', 'errNoneSplit');
      return Object.assign(out, {
        info: out.results.length === 1 ? pcT('infoCreated1') : pcT('infoCreatedN', out.results.length),
        note: toolNotes(out.results, uncovered.length ? pcT('splitUncovered', uncovered.join('; ')) : '')
      });
    }
  });
});

/* ---------- ROTATE ---------- */
pcOnPanel('rotate', function(){ // (v140) only once its panel is in the page (live, or made from its <template>)
  let selectedRotateDeg = 90;
  document.querySelectorAll('#rotate-options .quality-opt').forEach(opt=>{
    opt.addEventListener('click', ()=>{
      document.querySelectorAll('#rotate-options .quality-opt').forEach(o=>o.classList.remove('selected'));
      opt.classList.add('selected');
      selectedRotateDeg = parseInt(opt.dataset.deg, 10);
    });
  });

  /* v47: an optional page selection, added under the rotation choices by
     script (so no page markup had to change). Empty = every page. */
  const rotatePagesInput = (function(){
    const opts = document.getElementById('rotate-options');
    if(!opts) return null;
    const wrap = document.createElement('div');
    wrap.style.cssText = 'margin:12px 0 4px';
    const lab = document.createElement('label');
    lab.htmlFor = 'rotate-pages';
    lab.textContent = pcT('rotatePages');
    lab.style.cssText = 'display:block;font-size:0.85rem;margin-bottom:4px';
    const inp = document.createElement('input');
    inp.type = 'text'; inp.id = 'rotate-pages'; inp.autocomplete = 'off'; // (v119) no inputMode numeric: iOS' number pad has no "-" or ",
    inp.placeholder = '1, 3-5';
    inp.className = 'text-input'; // (v140) as the other page fields; copied from Split/Delete, which may not be in the page yet
    inp.style.cssText = 'width:100%;max-width:320px;box-sizing:border-box;padding:8px 10px;font-family:inherit'; // (v115) no font:inherit: it beat the 16px of .text-input on touch screens (iOS zooms in on smaller fields)
    wrap.appendChild(lab); wrap.appendChild(inp);
    opts.insertAdjacentElement('afterend', wrap);
    return inp;
  })();

  pcSetupTool('rotate', {
    accept: isPdfFile, working: () => pcT('stRotating'), zipName: 'rotated-pdf-files', fieldId: 'rotate-pages', reason: friendlyPdfError,
    run: async (list, _option, ctx) => {
      const opt = { deg: selectedRotateDeg, pages: rotatePagesInput ? rotatePagesInput.value : '' };
      const out = await pcEachPdfFile(list, ctx, (file, onProgress) => ctx.worker.run('rotatePdf', file, opt, onProgress), // (v119) in the worker
        'errSkipRotate', 'errNoneRotated');
      return Object.assign(out, { info: out.results.length === 1 ? pcT('infoRotated1') : pcT('infoRotatedN', out.results.length), note: toolNotes(out.results) });
    }
  });
});

/* ---------- PAGE NUMBERS ---------- */
pcOnPanel('numbers', function(){ // (v140) only once its panel is in the page (live, or made from its <template>)
  let selectedNumbersFormat = 'n';
  document.querySelectorAll('#numbers-format-options .quality-opt').forEach(opt=>{
    opt.addEventListener('click', ()=>{
      document.querySelectorAll('#numbers-format-options .quality-opt').forEach(o=>o.classList.remove('selected'));
      opt.classList.add('selected');
      selectedNumbersFormat = opt.dataset.format;
    });
  });

  /* v47: page-number options (position, start number, skip the cover),
     added by script so the HTML in 6 languages doesn't have to change. */
  const numbersOptions = (function(){
    const opts = document.getElementById('numbers-format-options');
    if(!opts) return null;
    const wrap = document.createElement('div');
    wrap.className = 'pc-num-opts';
    wrap.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px 20px;align-items:flex-end;margin:12px 0 4px;font-size:0.85rem';
    const field = (labelText, control)=>{
      const l = document.createElement('label');
      l.style.cssText = 'display:flex;flex-direction:column;gap:4px';
      const t = document.createElement('span'); t.textContent = labelText;
      l.appendChild(t); l.appendChild(control); return l;
    };
    const pos = document.createElement('select');
    pos.id = 'numbers-position';
    pos.className = 'text-input'; // (v115) 16px on touch screens, like the other fields
    pos.style.cssText = 'padding:7px 8px;font-family:inherit;max-width:220px';
    const keys = ['bottom-center','bottom-right','bottom-left','top-center','top-right','top-left'];
    pcT('numPosOpts').split('|').forEach((txt, i)=>{
      const o = document.createElement('option'); o.value = keys[i]; o.textContent = txt; pos.appendChild(o);
    });
    const start = document.createElement('input');
    // (v115) text, not number: an invalid value then reaches us instead of becoming '' (and silently 1)
    start.type = 'text'; start.id = 'numbers-start'; start.value = '1'; start.maxLength = 5; start.autocomplete = 'off';
    start.inputMode = 'numeric';
    start.className = 'text-input';
    start.style.cssText = 'width:90px;padding:7px 8px;font-family:inherit';
    const skipL = document.createElement('label');
    skipL.style.cssText = 'display:flex;gap:6px;align-items:center;padding-bottom:6px;cursor:pointer';
    const skip = document.createElement('input'); skip.type = 'checkbox'; skip.id = 'numbers-skip-first';
    const skipT = document.createElement('span'); skipT.textContent = pcT('numSkipFirst');
    skipL.appendChild(skip); skipL.appendChild(skipT);
    // (v157) size, colour and an optional page list, as at iLovePDF/PDF24
    const select = (id, values, labels, def) => {
      const s = document.createElement('select');
      s.id = id; s.className = 'text-input'; s.style.cssText = 'padding:7px 8px;font-family:inherit;max-width:160px';
      labels.split('|').forEach((txt, i) => { const o = document.createElement('option'); o.value = values[i]; o.textContent = txt; s.appendChild(o); });
      s.value = def;
      return s;
    };
    const size = select('numbers-size', ['small', 'medium', 'large'], pcT('numSizeOpts'), 'medium');
    const color = select('numbers-color', ['grey', 'black', 'white', 'blue', 'red'], pcT('numColorOpts'), 'grey');
    const pages = document.createElement('input');
    pages.type = 'text'; pages.id = 'numbers-pages'; pages.className = 'text-input'; pages.autocomplete = 'off';
    pages.placeholder = '1, 3-5'; pages.style.cssText = 'width:100%;max-width:320px;box-sizing:border-box;padding:7px 8px;font-family:inherit';
    wrap.appendChild(field(pcT('numPos'), pos));
    wrap.appendChild(field(pcT('numStart'), start));
    wrap.appendChild(field(pcT('numSize'), size));
    wrap.appendChild(field(pcT('numColor'), color));
    wrap.appendChild(skipL);
    const pagesField = field(pcT('rotatePages'), pages);
    pagesField.style.flexBasis = '100%';
    wrap.appendChild(pagesField);
    opts.insertAdjacentElement('afterend', wrap);
    return {
      input: start,
      // (v115) an invalid start number is a field error (it used to become 1 without a word)
      read(){
        if(!NUM_START_RE.test(start.value)) throw Object.assign(pcFieldError(pcT('numStartBad')), { fieldId: 'numbers-start' });
        let spec;
        try{ spec = pcParsePageSpec(pages.value); }catch(e){ if(e && e.field) e.fieldId = 'numbers-pages'; throw e; }
        return { position: pos.value || 'bottom-center', start: parseInt(start.value, 10), skipFirst: skip.checked,
          size: size.value, color: color.value, pages: spec };
      }
    };
  })();

  const NUM_START_RE = /^\s*\d{1,5}\s*$/; // 0 to 99999
  pcSetupTool('numbers', {
    accept: isPdfFile, working: () => pcT('stNumbering'), zipName: 'numbered-pdf-files', fieldId: ['numbers-start', 'numbers-pages'], reason: friendlyPdfError,
    run: async (list, _option, ctx) => {
      const opt = { format: selectedNumbersFormat, options: numbersOptions ? numbersOptions.read() : null }; // (v115) checked once, before any file
      const out = await pcEachPdfFile(list, ctx, (file, onProgress) => ctx.worker.run('numbersPdf', file, opt, onProgress), // (v119) in the worker
        'errSkipNumbers', 'errNoneProcessed');
      return Object.assign(out, { info: out.results.length === 1 ? pcT('infoNumbered1') : pcT('infoNumberedN', out.results.length), note: toolNotes(out.results) });
    }
  });
});

/* ---------- DELETE PAGES ---------- */
pcOnPanel('delete', function(){ // (v140) only once its panel is in the page (live, or made from its <template>)
  const pagesInput = document.getElementById('delete-pages');
  /* (v90) Removing pages has no page loop of its own: the bar shows its three
     steps (open, copy the pages that stay, save). */
  pcSetupTool('delete', {
    accept: isPdfFile, working: () => pcT('stRemoving'), zipName: 'pdf-pages-removed', fieldId: 'delete-pages', reason: friendlyPdfError,
    run: async (list, _option, ctx) => {
      const opt = { pages: pagesInput.value };
      const out = await pcEachPdfFile(list, ctx, (file, onProgress) => ctx.worker.run('deletePdf', file, opt, onProgress), // (v119) in the worker
        'errSkipDelete', 'errNoneProcessed');
      return Object.assign(out, { info: out.results.length === 1 ? pcT('infoUpdated1') : pcT('infoUpdatedN', out.results.length), note: toolNotes(out.results) });
    }
  });
});
